import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, message, Modal } from 'antd'
import { useIntl } from 'react-intl'
import localStore from 'store'
import { useDispatch, useSelector } from 'react-redux'
import { locationSelector, loginFormSelector, userSelector } from 'app-redux/selectors'
import { afterLoginHandler } from 'app-redux/user/actions'
import { setSelectedEndpoint } from 'app-redux/companies/actions'
import { singleLogin, verifyGoogleToken } from 'utils/apis'
import { setRefreshToken, setSavedEmail, setToken } from 'utils/cookies'
import { isSafeToReloadPath } from 'utils/appUpdate'
import LoginForm from 'components/apps/User/Login'
import Sso from 'containers/Users/Login/Sso'
import { closeLoginForm, setErrorLoginForm } from 'app-redux/loginSlice'
import { isEmpty } from 'lodash-es'
import LoginOTP from 'components/apps/User/Login/LoginOTP'
import TwoFactorVerification from 'components/apps/User/Login/TwoFactorVerification'
import TwoFactorRequired from './TwoFactorRequired' // Diparsing baseurl nya sesuai di base url axios

// Diparsing baseurl nya sesuai di base url axios
const parseBaseUrl = (url) => {
  let baseURL = url.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '')
  baseURL = baseURL.replace(/\/api\/v1+$/, '')
  return baseURL
}

const LoginModal = () => {
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()
  const user = useSelector(userSelector)
  const loginForm = useSelector(loginFormSelector)
  const location = useSelector(locationSelector)

  const [loginType, setLoginType] = useState('')
  const [loading, setLoading] = useState(false)
  const [isTemporary, setIsTemporary] = useState(false)

  // Untuk pengecekan posisi halaman di form atau tidak
  const isSafeToReload = useMemo(() => isSafeToReloadPath(location.pathname), [location.pathname])

  useEffect(() => {
    if (!loginForm.showLoginForm && !isEmpty(loginForm.error)) {
      dispatch(setErrorLoginForm(null))
    }
  }, [dispatch, loginForm.error, loginForm.showLoginForm])

  const hideHandler = useCallback(() => dispatch(closeLoginForm()), [dispatch])
  const handleLogin = useCallback(
    async (values) => {
      try {
        setLoading(true)
        setSavedEmail(values.email)
        // dispatch(setErrorLoginForm(null));
        const response = await singleLogin(values.email, values.password, values.remember_me)
        setLoading(false)
        if (response.data.success) {
          const resData = response.data.data || {}
          if (resData.is_2fa_active) {
            setLoginType('2fa')
          } else if (`${resData.otp_required}` === '1') {
            setLoginType('otp_required')
            if (`${resData.temporary_login}` === '1') {
              setIsTemporary(true)
            }
          } else {
            // Jika dari api tidak ada return endpoint, untuk ambil endpoin dari config
            if (!response.data?.data?.init?.endpoint && !localStore.get('app.endpoint')) {
              response.data.data.endpoint = parseBaseUrl(response.config?.baseURL)
            }
            // Sebelum reload dicek dulu apakah posisi input data
            if (isSafeToReload) {
              dispatch(afterLoginHandler(response, {}, true))
              window.location.reload(true)
            } else {
              if (user.email === response.data?.data?.user?.email) {
                const tokenData = response.data?.data?.data
                if (tokenData?.access_token) {
                  setToken(tokenData.access_token)
                }
                if (tokenData?.refresh_token) {
                  setRefreshToken(tokenData.refresh_token)
                }
                // Jika dapat endpoint dari login dan di localstoragenya kosong
                if (response.data?.data?.init?.endpoint && !localStore.get('app.endpoint')) {
                  dispatch(setSelectedEndpoint(response.data?.data?.init.endpoint))
                }
              } else {
                dispatch(afterLoginHandler(response, {}, true))
              }
              hideHandler()
            }
          }
        } else {
          message.error(response.data.message)
        }
      } catch (e) {
        setLoading(false)
        if (e && e.data && e.data.message) {
          dispatch(setErrorLoginForm(e.data.message))
        }
      }
    },
    [dispatch, hideHandler, isSafeToReload, user],
  )

  const onGoogleLogin = useCallback(
    async (payload) => {
      if (payload?.code) {
        try {
          setLoading(true)
          const response = await verifyGoogleToken(payload.code)
          setLoading(false)
          if (response.data.success) {
            // if (user.email === response.data.data.user.email) {
            //   setSavedEmail(response.data.data.user.email);
            //   setToken(response.data.data.data.access_token);
            // } else {
            //   dispatch(afterLoginHandler(response, { email: response.data.data.user.email }, true));
            // }
            // hideHandler();
            // Jika dari api tidak ada return endpoint, untuk ambil endpoin dari config
            if (!response.data?.data?.init?.endpoint && !localStore.get('app.endpoint')) {
              response.data.data.endpoint = parseBaseUrl(response.config?.baseURL)
            }
            // Sebelum reload dicek dulu apakah posisi input data
            if (isSafeToReload) {
              dispatch(afterLoginHandler(response, { email: response.data.data.user.email }, true))
              window.location.reload(true)
            } else {
              if (user.email === response.data?.data?.user?.email) {
                const tokenData = response.data?.data?.data
                if (tokenData?.access_token) {
                  setToken(tokenData.access_token)
                }
                if (tokenData?.refresh_token) {
                  setRefreshToken(tokenData.refresh_token)
                }
                // Jika dapat endpoint dari login dan di localstoragenya kosong
                if (response.data?.data?.init?.endpoint && !localStore.get('app.endpoint')) {
                  dispatch(setSelectedEndpoint(response.data?.data?.init.endpoint))
                }
              } else {
                dispatch(
                  afterLoginHandler(response, { email: response.data.data.user.email }, true),
                )
              }
              hideHandler()
            }
          } else {
            message.error(response.data.message)
          }
        } catch (e) {
          setLoading(false)
          if (e && e.data && e.data.message) {
            dispatch(setErrorLoginForm(e.data.message))
          }
        }
      }
    },
    [dispatch, hideHandler, isSafeToReload, user.email],
  )

  const resetLoginType = useCallback(() => setLoginType(''), [])
  const ssoLogin = useCallback(() => setLoginType('sso'), [])
  const otpLogin = useCallback(() => setLoginType('otp'), [])

  return (
    <Modal
      open={loginForm.showLoginForm}
      onCancel={hideHandler}
      footer={null}
      closable={false}
      maskClosable={false}
      destroyOnHidden
    >
      {!loginType && (
        <h5 className="text-uppercase">
          <strong>
            {loginForm.message || formatMessage({ id: 'users.you_have_logged_out_automatically' })}
          </strong>
        </h5>
      )}

      {!isEmpty(loginForm.error) && (
        <Alert message={loginForm.error} type="error" showIcon className="mt-3" />
      )}
      <br />
      {loginType === 'sso' && <Sso onBackLogin={resetLoginType} />}
      {loginType === 'otp' && <LoginOTP onBackCallback={resetLoginType} isOnModal />}
      {loginType === '2fa' && (
        <TwoFactorVerification email={user?.email} onBack={resetLoginType} isOnModal />
      )}
      {loginType === 'otp_required' && (
        <TwoFactorRequired email={user?.email} isOnModal isTemporary={isTemporary} />
      )}
      {!loginType && (
        <LoginForm
          loading={loading}
          onSubmit={handleLogin}
          onGoogleLogin={onGoogleLogin}
          onSsoLogin={ssoLogin}
          onOtpLogin={otpLogin}
        />
      )}
    </Modal>
  )
}

export default LoginModal
